<script setup lang="ts">
// ================================
// L'ENCART DE RETOURS (D31, maquette mq-retours, partie 8 option B).
//
// Ce que le lecteur doit vivre : sous son résultat, une question courte,
// repliée, qui ne bloque rien ; trois questions fermées (deux sur un refus), un
// champ libre facultatif ; un envoi, un remerciement. Jamais en fenêtre,
// jamais avant un résultat, jamais de lui-même : la page ne le montre qu'au
// clic sur « Donner votre avis sur cette étude » (décision du 06/10/2026).
//
// L'envoi passe par la route de support existante (`SupportService`,
// `POST /api/metier/v5/support`, type « Feedback ») ; la règle et la charge
// sont dans `optim-turpe-v2.retours.ts`. La page montre l'encart au clic,
// garde le journal et émet l'événement de mesure (`envoye`).
// ================================
import { computed, ref, watch } from "vue"
import { SupportService } from "@core/services/support.service"
import {
    NIVEAU_1,
    NIVEAU_3,
} from "@modules/courtage/utils/optim-turpe-v2.boutons"
import {
    OPTIONS_CLARTE,
    OPTIONS_INTENTION,
    OPTIONS_JUSTESSE,
    OPTIONS_REFUS,
    TEXTES_RETOURS as T,
    construireRetour,
    libelleEnvoyeLe,
    peutEnvoyer,
    reponsesVides,
    type EnvoiRetours,
    type ReponsesRetours,
    type VarianteRetours,
} from "@modules/courtage/utils/optim-turpe-v2.retours"

type Props = {
    etudeId: string
    variante: VarianteRetours
    /** État servi du résultat, ou code du refus servi. */
    etat: string | null
    /** L'avis déjà envoyé pour cette étude, s'il y en a un. */
    envoi?: EnvoiRetours | null
}
const props = withDefaults(defineProps<Props>(), { envoi: null })

const ouvert = defineModel<boolean>("ouvert", { default: false })

const emit = defineEmits<{
    envoye: [reponses: ReponsesRetours]
}>()

const service = new SupportService()

const reponses = ref<ReponsesRetours>(reponsesVides())
const envoiEnCours = ref(false)
const echec = ref(false)
/** L'envoi de cette session, en attendant que la page le note au journal. */
const envoiLocal = ref<EnvoiRetours | null>(null)

watch(
    () => props.etudeId,
    () => {
        reponses.value = reponsesVides()
        envoiLocal.value = null
        echec.value = false
    }
)

const getEnvoi = computed(() => props.envoi ?? envoiLocal.value)

const getEntete = computed(() => {
    if (getEnvoi.value) {
        return {
            titre: T.enteteEnvoye,
            complement: libelleEnvoyeLe(getEnvoi.value.le),
        }
    }
    return props.variante === "refus"
        ? { titre: T.enteteRefus, complement: T.dureeRefus }
        : { titre: T.enteteResultat, complement: T.dureeResultat }
})

const getPeutEnvoyer = computed(
    () => peutEnvoyer(reponses.value) && !envoiEnCours.value
)

async function onEnvoyer(): Promise<void> {
    if (!getPeutEnvoyer.value) return
    envoiEnCours.value = true
    echec.value = false
    try {
        await service.sendFeedback(
            construireRetour(
                {
                    etudeId: props.etudeId,
                    variante: props.variante,
                    etat: props.etat,
                },
                reponses.value
            )
        )
        envoiLocal.value = { le: new Date().toISOString() }
        emit("envoye", { ...reponses.value })
    } catch {
        echec.value = true
    } finally {
        envoiEnCours.value = false
    }
}

function nom(question: string): string {
    return `optim-turpe-v2-retours-${question}-${props.etudeId}`
}

/** Une question fermée : sa clé dans les réponses, sa légende, ses options. */
type QuestionFermee = {
    cle: "clarte" | "justesse" | "intention" | "refusJustifie"
    legende: string
    options: { valeur: string; libelle: string }[]
}

/**
 * Revue 2 en production (I74) : les questions fermées, dans l'ordre, avec le
 * MÊME contrôle (bouton radio PrimeVue en liste) ; deux sur un refus, trois
 * sur un résultat.
 */
const getQuestions = computed<QuestionFermee[]>(() => [
    { cle: "clarte", legende: T.clarte, options: OPTIONS_CLARTE },
    ...(props.variante === "refus"
        ? [
              {
                  cle: "refusJustifie" as const,
                  legende: T.refusJustifie,
                  options: OPTIONS_REFUS,
              },
          ]
        : [
              {
                  cle: "justesse" as const,
                  legende: T.justesse,
                  options: OPTIONS_JUSTESSE,
              },
              {
                  cle: "intention" as const,
                  legende: T.intention,
                  options: OPTIONS_INTENTION,
              },
          ]),
])

/** Pose la réponse d'une question fermée (valeur prise dans ses options). */
function onRepondre(cle: QuestionFermee["cle"], valeur: string): void {
    reponses.value = { ...reponses.value, [cle]: valeur }
}
</script>

<template>
    <!-- Revue 2 (I76) : « Plus tard » replie TOUT ; le lien « Donner votre
         avis sur cette étude » reste la seule entrée. -->
    <div
        v-if="ouvert || getEnvoi"
        id="acc-retours"
        class="optim-turpe-v2-retours scroll-mt-36 rounded-lg border border-surface-200 bg-white"
        data-testid="optim-turpe-v2-retours"
        :data-variante="variante"
        :data-envoye="getEnvoi ? '1' : '0'"
    >
        <button
            type="button"
            class="flex w-full items-center justify-between gap-3 px-5 py-3 text-left"
            :aria-expanded="ouvert"
            data-testid="optim-turpe-v2-retours-entete"
            @click="ouvert = !ouvert"
        >
            <span class="text-sm font-medium text-primary-800"
                >{{ getEntete.titre }} ·
                <span class="font-normal text-primary-500">{{
                    getEntete.complement
                }}</span></span
            >
            <BaseIcon
                :icon="ouvert ? 'pi pi-chevron-up' : 'pi pi-chevron-down'"
                size="0.8rem"
                class="text-primary-400"
                aria-hidden="true"
            />
        </button>

        <!-- Après l'envoi : le remerciement, à la place des questions. -->
        <div
            v-if="getEnvoi"
            class="flex flex-col gap-1 px-5 pb-4"
            data-testid="optim-turpe-v2-retours-merci"
        >
            <p class="text-sm font-medium text-primary-800">{{ T.merci }}</p>
        </div>

        <form
            v-else-if="ouvert"
            class="flex flex-col gap-4 px-5 pb-5"
            data-testid="optim-turpe-v2-retours-questions"
            @submit.prevent
        >
            <fieldset
                v-for="question in getQuestions"
                :key="question.cle"
                class="flex flex-col gap-1.5"
                :data-testid="`optim-turpe-v2-retours-question-${question.cle}`"
            >
                <legend class="mb-1 text-sm text-primary-800">
                    {{ question.legende }}
                </legend>
                <div
                    v-for="o in question.options"
                    :key="o.valeur"
                    class="flex items-center gap-2"
                >
                    <BaseRadioButton
                        :model-value="reponses[question.cle]"
                        :input-id="`${nom(question.cle)}-${o.valeur}`"
                        :name="nom(question.cle)"
                        :value="o.valeur"
                        :data-testid="`optim-turpe-v2-retours-${question.cle}`"
                        @update:model-value="onRepondre(question.cle, $event)"
                    />
                    <label
                        :for="`${nom(question.cle)}-${o.valeur}`"
                        class="text-sm text-surface-700"
                        >{{ o.libelle }}</label
                    >
                </div>
            </fieldset>

            <fieldset class="flex flex-col gap-1">
                <legend class="mb-1 text-sm text-primary-800">
                    {{ T.manque }}
                    <span class="text-xs text-primary-500">{{
                        T.facultatif
                    }}</span>
                </legend>
                <textarea
                    v-model="reponses.manque"
                    rows="3"
                    maxlength="2000"
                    class="rounded-md border border-surface-300 p-2 text-sm"
                    :aria-label="T.manque"
                    data-testid="optim-turpe-v2-retours-manque"
                />
                <span class="text-xs text-primary-500">{{ T.manqueAide }}</span>
            </fieldset>

            <div class="flex flex-wrap items-center gap-2">
                <BaseButton
                    v-bind="NIVEAU_1"
                    :label="envoiEnCours ? T.envoiEnCours : T.envoyer"
                    :disabled="!getPeutEnvoyer"
                    :loading="envoiEnCours"
                    data-testid="optim-turpe-v2-retours-envoyer"
                    @click="onEnvoyer"
                />
                <BaseButton
                    v-bind="NIVEAU_3"
                    :label="T.plusTard"
                    data-testid="optim-turpe-v2-retours-plus-tard"
                    @click="ouvert = false"
                />
                <span
                    v-if="echec"
                    class="text-xs text-[var(--p-orange-700)]"
                    data-testid="optim-turpe-v2-retours-echec"
                    >{{ T.echecEnvoi }}</span
                >
            </div>
        </form>
    </div>
</template>
